import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import UsernameModal from "../../modal/UsernameModal";
import { splitName } from "../../utils";
import { toast } from "react-toastify";
import { updateTicketNamesApi } from "../../services/nameUpdateServices";
import Loader from "../../compoment/ui/Loader";
import ScreenHeader from "../../compoment/ui/ScreenHeader";
import CircleButton from "../../compoment/ui/CircleButton";
import { ArrowRight } from "../../compoment/icon";
import {
  getTicketDetailFromStorage,
  setTicketDetailInStorage,
} from "../../utils/ticket";
import { WISH_MAX } from "../../utils/wish";

const MainScreen = () => {
    const navigate = useNavigate();
    const [nameModal, setNameModal] = useState(false);
    const [name, setName] = useState("");
    const [nameErr, setNameErr] = useState("");
    // Pre-filled when coming back from the review screen via EDIT.
    const [wish, setWish] = useState(() => localStorage.getItem("wish") ?? "");
    const [wishErr, setWishErr] = useState("");
    const [loading, setLoading] = useState<boolean>(false);
    const [ticketData, setTicketData] = useState<any>({});

    useEffect(() => {
        const ticket = getTicketDetailFromStorage();
        if (ticket) {
            setTicketData(ticket);
        }
    }, []);


    // Handlers
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
        if (nameErr) setNameErr(""); // clear error on typing
    };

    const handleWishChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.length <= WISH_MAX) {
            setWish(value);
            if (wishErr) setWishErr("");
        }
    };

    useEffect(() => {
        const navigationState = window.history.state;
        if (ticketData && ticketData?.first_name && ticketData?.last_name) {
            const fullName = `${ticketData?.first_name} ${ticketData?.last_name}`;
            setName(fullName);
            setNameModal(false);
        } else if (navigationState?.userName) {
            setName(navigationState.userName);
            setNameModal(false);
        } else {
            setNameModal(true);
        }
    }, [ticketData, ticketData?.first_name, ticketData?.last_name]);

    // handle username submit
    const handleSubmit = async () => {
        if (!name.trim()) {
            setNameErr("Name field is required.");
            return;
        }
        setNameErr("");
        const { firstName, lastName } = splitName(name);

        if (!ticketData?.id) {
            toast.error("Invalid ticket data.");
            return;
        }

        try {
            setLoading(true);
            const response = await updateTicketNamesApi(ticketData.id, {
                first_name: firstName,
                last_name: lastName || " ",
            });

            if (response?.success) {
                const updatedTicket = {
                    ...ticketData,
                    first_name: firstName,
                    last_name: lastName || " ",
                };
                setTicketDetailInStorage(updatedTicket);
                toast.success("Ticket name updated successfully.");
            } else {
                console.error("Failed to update ticket names:", response?.error);
                toast.error(response?.message || "Error updating ticket names.");
            }
        } catch (error) {
            console.error("Error updating ticket names:", error);
            toast.error("Something went wrong while updating the ticket name.");
        } finally {
            setLoading(false);
            setNameModal(false);
        }
    };

    // handle wish submit
    const handleNext = () => {
        if (!wish.trim()) {
            setWishErr("Please enter your wish.");
            return;
        }
        if (wish.length > WISH_MAX) {
            setWishErr(`Wish text cannot exceed ${WISH_MAX} characters.`);
            return;
        }
        localStorage.setItem('wish', wish)
        navigate("/selected-lantern");
    };

    return (
        <>
            {(loading) && <Loader />}
            <section className="wishScreen wishScreen--entry">
                <ScreenHeader />

                <main className="wishScreen__copy">
                    <h1 className="wishScreen__title">
                        WHAT IS
                        <br />
                        YOUR WISH?
                    </h1>
                    <p className="wishScreen__prompt">
                        Write your wish and send it
                        <br />
                        into the sky.
                    </p>
                </main>

                <div className="wishField">
                    <label className={`wishField__control${wishErr ? " is-error" : ""}`}>
                        <span className="wishField__icon" aria-hidden="true">
                            T
                        </span>
                        <input
                            className="wishField__input"
                            type="text"
                            name="wishTxt"
                            id="wishTxt"
                            placeholder="Write your wish..."
                            aria-label="Write your wish"
                            aria-invalid={wishErr ? "true" : "false"}
                            value={wish}
                            maxLength={WISH_MAX}
                            onChange={handleWishChange}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleNext();
                            }}
                            autoComplete="off"
                        />
                        <span className="wishField__count" aria-live="polite">
                            {wish.length}/{WISH_MAX}
                        </span>
                    </label>
                    {wishErr ? (
                        <p className="wishField__error" role="alert">
                            {wishErr}
                        </p>
                    ) : null}
                </div>

                <CircleButton label="NEXT" icon={<ArrowRight />} onClick={handleNext} />
            </section>

            {/* Username Modal */}
            {nameModal && (
                <UsernameModal
                    show={nameModal}
                    onHide={() => setNameModal(false)}
                    handleSubmit={handleSubmit}
                    name={name}
                    setName={setName}
                    nameErr={nameErr}
                    setNameErr={setNameErr}
                    handleChange={handleNameChange}
                />
            )}
        </>
    );
};

export default MainScreen;
